import { PredefinedStylesType } from './tablesEnum/predefinedStylesTypeEnum';
import { PredefinedTableStyles } from './tablesEnum/predefinedTableStylesEnum';
import { TableBorderFormat, TableFormat } from 'roosterjs-editor-types';
import { TableColors } from './tablesEnum/tableColors';

const PREDEFINED_STYLES_TYPE: Record<string, (color: string) => TableFormat> = {
    DEFAULT: color =>
        createTableFormat(
            color /**topBorder */,
            color /**bottomBorder */,
            color /** verticalColors*/,
            false /** bandedRows */,
            false /** bandedColumns */,
            false /** headerRow */,
            false /** firstColumn */,
            TableBorderFormat.DEFAULT /** tableBorderFormat */,
            null /** bgColorEven */,
            lightColors(color) /** bgColorOdd */,
            color /** headerRowColor */
        ),
    GRID: color =>
        createTableFormat(
            color /**topBorder */,
            color /**bottomBorder */,
            color /** verticalColors*/,
            true /** bandedRows */,
            false /** bandedColumns */,
            false /** headerRow */,
            false /** firstColumn */,
            TableBorderFormat.DEFAULT /** tableBorderFormat */,
            null /** bgColorEven */,
            lightColors(color) /** bgColorOdd */,
            color /** headerRowColor */
        ),
    GRID_WITH_HEADER_ROW: color =>
        createTableFormat(
            color /**topBorder */,
            color /**bottomBorder */,
            color /** verticalColors*/,
            true /** bandedRows */,
            false /** bandedColumns */,
            true /** headerRow */,
            false /** firstColumn */,
            TableBorderFormat.DEFAULT /** tableBorderFormat */,
            null /** bgColorEven */,
            lightColors(color) /** bgColorOdd */,
            color /** headerRowColor */
        ),
    ESPECIAL_GRID: color =>
        createTableFormat(
            color /**topBorder */,
            color /**bottomBorder */,
            color /** verticalColors*/,
            true /** bandedRows */,
            false /** bandedColumns */,
            false /** headerRow */,
            false /** firstColumn */,
            TableBorderFormat.ESPECIAL_TYPE_1 /** tableBorderFormat */,
            null /** bgColorEven */,
            lightColors(color) /** bgColorOdd */,
            color /** headerRowColor */
        ),
    LIST: color =>
        createTableFormat(
            color /**topBorder */,
            color /**bottomBorder */,
            TableColors.transparent /** verticalColors*/,
            true /** bandedRows */,
            false /** bandedColumns */,
            false /** headerRow */,
            false /** firstColumn */,
            TableBorderFormat.DEFAULT /** tableBorderFormat */,
            null /** bgColorEven */,
            lightColors(color) /** bgColorOdd */,
            color /** headerRowColor */
        ),
    LIST_WITH_HEADER: color =>
        createTableFormat(
            color /**topBorder */,
            color /**bottomBorder */,
            color /** verticalColors*/,
            false /** bandedRows */,
            false /** bandedColumns */,
            true /** headerRow */,
            false /** firstColumn */,
            TableBorderFormat.LIST_WITH_SIDE_BORDERS /** tableBorderFormat */,
            null /** bgColorEven */,
            lightColors(color) /** bgColorOdd */,
            color /** headerRowColor */
        ),
    LIST_ESPECIAL: color =>
        createTableFormat(
            color /**topBorder */,
            color /**bottomBorder */,
            color /** verticalColors*/,
            true /** bandedRows */,
            false /** bandedColumns */,
            false /** headerRow */,
            true /** firstColumn */,
            TableBorderFormat.ESPECIAL_TYPE_2 /** tableBorderFormat */,
            null /** bgColorEven */,
            lightColors(color) /** bgColorOdd */,
            color /** headerRowColor */
        ),
    PLAIN: color =>
        createTableFormat(
            color /**topBorder */,
            color /**bottomBorder */,
            color /** verticalColors*/,
            true /** bandedRows */,
            false /** bandedColumns */,
            false /** headerRow */,
            false /** firstColumn */,
            TableBorderFormat.ESPECIAL_TYPE_3 /** tableBorderFormat */,
            null /** bgColorEven */,
            lightColors(color) /** bgColorOdd */,
            color /** headerRowColor */
        ),
    PLAIN_LIST: color =>
        createTableFormat(
            color /**topBorder */,
            color /**bottomBorder */,
            TableColors.transparent /** verticalColors*/,
            false /** bandedRows */,
            false /** bandedColumns */,
            false /** headerRow */,
            false /** firstColumn */,
            TableBorderFormat.DEFAULT /** tableBorderFormat */,
            null /** bgColorEven */,
            lightColors(color) /** bgColorOdd */,
            color /** headerRowColor */
        ),
    PLAIN_WITHOUT_BORDERS: color =>
        createTableFormat(
            TableColors.transparent /**topBorder */,
            TableColors.transparent /**bottomBorder */,
            TableColors.transparent /** verticalColors*/,
            true /** bandedRows */,
            false /** bandedColumns */,
            false /** headerRow */,
            false /** firstColumn */,
            TableBorderFormat.DEFAULT /** tableBorderFormat */,
            null /** bgColorEven */,
            lightColors(color) /** bgColorOdd */,
            color /** headerRowColor */
        ),
    PLAIN_WITH_FIRST_COLUMN: color =>
        createTableFormat(
            color /**topBorder */,
            color /**bottomBorder */,
            color /** verticalColors*/,
            true /** bandedRows */,
            false /** bandedColumns */,
            false /** headerRow */,
            true /** firstColumn */,
            TableBorderFormat.ESPECIAL_TYPE_3 /** tableBorderFormat */,
            null /** bgColorEven */,
            lightColors(color) /** bgColorOdd */,
            color /** headerRowColor */
        ),
};

function createTableFormat(
    topBorder?: string,
    bottomBorder?: string,
    verticalBorder?: string,
    bandedRows?: boolean,
    bandedColumns?: boolean,
    headerRow?: boolean,
    firstColumn?: boolean,
    borderFormat?: TableBorderFormat,
    bgColorEven?: string | null,
    bgColorOdd?: string,
    headerRowColor?: string
): TableFormat {
    return {
        topBorderColor: topBorder,
        bottomBorderColor: bottomBorder,
        verticalBorderColor: verticalBorder,
        hasBandedRows: bandedRows,
        bgColorEven: bgColorEven,
        bgColorOdd: bgColorOdd,
        hasBandedColumns: bandedColumns,
        hasHeaderRow: headerRow,
        headerRowColor: headerRowColor,
        hasFirstColumn: firstColumn,
        tableBorderFormat: borderFormat,
    };
}

const lightColors = (color: string) => {
    return `${color}20`;
};

export const PREDEFINED_STYLES: Record<string, TableFormat> = {
    [PredefinedTableStyles.defaultBlack]: PREDEFINED_STYLES_TYPE[PredefinedStylesType.default](
        TableColors.black
    ),
    [PredefinedTableStyles.defaultGray]: PREDEFINED_STYLES_TYPE[PredefinedStylesType.default](
        TableColors.gray
    ),
    [PredefinedTableStyles.gridGray]: PREDEFINED_STYLES_TYPE[PredefinedStylesType.grid](
        TableColors.gray
    ),
    [PredefinedTableStyles.plainListBlack]: PREDEFINED_STYLES_TYPE[PredefinedStylesType.plainList](
        TableColors.black
    ),
    [PredefinedTableStyles.plainBlack]: PREDEFINED_STYLES_TYPE[PredefinedStylesType.plain](
        TableColors.black
    ),
    [PredefinedTableStyles.plainWithoutBordersBlack]: PREDEFINED_STYLES_TYPE[
        PredefinedStylesType.plainWithoutBorders
    ](TableColors.black),
    [PredefinedTableStyles.plainWithFirstColumnBlack]: PREDEFINED_STYLES_TYPE[
        PredefinedStylesType.plainWithFirstColumn
    ](TableColors.black),
    [PredefinedTableStyles.gridBlack]: PREDEFINED_STYLES_TYPE[PredefinedStylesType.grid](
        TableColors.black
    ),
    [PredefinedTableStyles.gridBlue]: PREDEFINED_STYLES_TYPE[PredefinedStylesType.grid](
        TableColors.blue
    ),
    [PredefinedTableStyles.gridOrange]: PREDEFINED_STYLES_TYPE[PredefinedStylesType.grid](
        TableColors.orange
    ),
    [PredefinedTableStyles.gridYellow]: PREDEFINED_STYLES_TYPE[PredefinedStylesType.grid](
        TableColors.yellow
    ),
    [PredefinedTableStyles.gridRed]: PREDEFINED_STYLES_TYPE[PredefinedStylesType.grid](
        TableColors.red
    ),
    [PredefinedTableStyles.gridPurple]: PREDEFINED_STYLES_TYPE[PredefinedStylesType.grid](
        TableColors.purple
    ),
    [PredefinedTableStyles.gridGreen]: PREDEFINED_STYLES_TYPE[PredefinedStylesType.grid](
        TableColors.green
    ),
    [PredefinedTableStyles.especialGridBlack]: PREDEFINED_STYLES_TYPE[
        PredefinedStylesType.especialGrid
    ](TableColors.black),
    [PredefinedTableStyles.especialGridBlue]: PREDEFINED_STYLES_TYPE[
        PredefinedStylesType.especialGrid
    ](TableColors.blue),
    [PredefinedTableStyles.especialGridOrange]: PREDEFINED_STYLES_TYPE[
        PredefinedStylesType.especialGrid
    ](TableColors.orange),
    [PredefinedTableStyles.especialGridYellow]: PREDEFINED_STYLES_TYPE[
        PredefinedStylesType.especialGrid
    ](TableColors.yellow),
    [PredefinedTableStyles.especialGridRed]: PREDEFINED_STYLES_TYPE[
        PredefinedStylesType.especialGrid
    ](TableColors.red),
    [PredefinedTableStyles.especialGridPurple]: PREDEFINED_STYLES_TYPE[
        PredefinedStylesType.especialGrid
    ](TableColors.purple),
    [PredefinedTableStyles.especialGridGreen]: PREDEFINED_STYLES_TYPE[
        PredefinedStylesType.especialGrid
    ](TableColors.green),
    [PredefinedTableStyles.gridWithHeaderBlack]: PREDEFINED_STYLES_TYPE[
        PredefinedStylesType.gridWithHeader
    ](TableColors.black),
    [PredefinedTableStyles.gridWithHeaderBlue]: PREDEFINED_STYLES_TYPE[
        PredefinedStylesType.gridWithHeader
    ](TableColors.blue),
    [PredefinedTableStyles.gridWithHeaderOrange]: PREDEFINED_STYLES_TYPE[
        PredefinedStylesType.gridWithHeader
    ](TableColors.orange),
    [PredefinedTableStyles.gridWithHeaderYellow]: PREDEFINED_STYLES_TYPE[
        PredefinedStylesType.gridWithHeader
    ](TableColors.yellow),
    [PredefinedTableStyles.gridWithHeaderRed]: PREDEFINED_STYLES_TYPE[
        PredefinedStylesType.gridWithHeader
    ](TableColors.red),
    [PredefinedTableStyles.gridWithHeaderPurple]: PREDEFINED_STYLES_TYPE[
        PredefinedStylesType.gridWithHeader
    ](TableColors.purple),
    [PredefinedTableStyles.gridWithHeaderGreen]: PREDEFINED_STYLES_TYPE[
        PredefinedStylesType.gridWithHeader
    ](TableColors.green),
    [PredefinedTableStyles.listBlack]: PREDEFINED_STYLES_TYPE[PredefinedStylesType.list](
        TableColors.black
    ),
    [PredefinedTableStyles.listBlue]: PREDEFINED_STYLES_TYPE[PredefinedStylesType.list](
        TableColors.blue
    ),
    [PredefinedTableStyles.listOrange]: PREDEFINED_STYLES_TYPE[PredefinedStylesType.list](
        TableColors.orange
    ),
    [PredefinedTableStyles.listYellow]: PREDEFINED_STYLES_TYPE[PredefinedStylesType.list](
        TableColors.yellow
    ),
    [PredefinedTableStyles.listRed]: PREDEFINED_STYLES_TYPE[PredefinedStylesType.list](
        TableColors.red
    ),
    [PredefinedTableStyles.listPurple]: PREDEFINED_STYLES_TYPE[PredefinedStylesType.list](
        TableColors.purple
    ),
    [PredefinedTableStyles.listGreen]: PREDEFINED_STYLES_TYPE[PredefinedStylesType.list](
        TableColors.green
    ),
    [PredefinedTableStyles.listWithHeaderBlack]: PREDEFINED_STYLES_TYPE[
        PredefinedStylesType.listWithHeader
    ](TableColors.black),
    [PredefinedTableStyles.listWithHeaderBlue]: PREDEFINED_STYLES_TYPE[
        PredefinedStylesType.listWithHeader
    ](TableColors.blue),
    [PredefinedTableStyles.listWithHeaderOrange]: PREDEFINED_STYLES_TYPE[
        PredefinedStylesType.listWithHeader
    ](TableColors.orange),
    [PredefinedTableStyles.listWithHeaderYellow]: PREDEFINED_STYLES_TYPE[
        PredefinedStylesType.listWithHeader
    ](TableColors.yellow),
    [PredefinedTableStyles.listWithHeaderRed]: PREDEFINED_STYLES_TYPE[
        PredefinedStylesType.listWithHeader
    ](TableColors.red),
    [PredefinedTableStyles.listWithHeaderPurple]: PREDEFINED_STYLES_TYPE[
        PredefinedStylesType.listWithHeader
    ](TableColors.purple),
    [PredefinedTableStyles.listWithHeaderGreen]: PREDEFINED_STYLES_TYPE[
        PredefinedStylesType.listWithHeader
    ](TableColors.green),
    [PredefinedTableStyles.listEspecialBlack]: PREDEFINED_STYLES_TYPE[
        PredefinedStylesType.listEspecial
    ](TableColors.black),
    [PredefinedTableStyles.listEspecialBlue]: PREDEFINED_STYLES_TYPE[
        PredefinedStylesType.listEspecial
    ](TableColors.blue),
    [PredefinedTableStyles.listEspecialOrange]: PREDEFINED_STYLES_TYPE[
        PredefinedStylesType.listEspecial
    ](TableColors.orange),
    [PredefinedTableStyles.listEspecialYellow]: PREDEFINED_STYLES_TYPE[
        PredefinedStylesType.listEspecial
    ](TableColors.yellow),
    [PredefinedTableStyles.listEspecialRed]: PREDEFINED_STYLES_TYPE[
        PredefinedStylesType.listEspecial
    ](TableColors.red),
    [PredefinedTableStyles.listEspecialPurple]: PREDEFINED_STYLES_TYPE[
        PredefinedStylesType.listEspecial
    ](TableColors.purple),
    [PredefinedTableStyles.listEspecialGreen]: PREDEFINED_STYLES_TYPE[
        PredefinedStylesType.listEspecial
    ](TableColors.green),
};
